import { queryOptions, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ScrapedPage {
  id: string;
  type: string;
  slug: string;
  path: string;
  title: string | null;
  meta_description: string | null;
  /** Originaltitel (<title>) hämtad från elmarknad.se. */
  seo_title?: string | null;
  /** Original meta description hämtad från elmarknad.se. */
  seo_description?: string | null;
  content_md: string | null;
  content_html: string | null;
  hero_image: string | null;
  images: unknown;
  data: unknown;
  source_url: string;
  lastmod: string | null;
  scraped_at: string | null;
  is_published: boolean;
  /** Publiceringsdatum (parsat från artikeln eller lastmod). */
  published_at?: string | null;
}

const STALE_TIME = 5 * 60 * 1000;

/** Query options för en publicerad skrapad sida – återanvänds av route loaders (SSR). */
export function scrapedPageQueryOptions(path: string) {
  return queryOptions({
    queryKey: ["scraped-page", path],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scraped_pages")
        .select("*")
        .eq("path", path)
        .eq("is_published", true)
        .maybeSingle();
      if (error) throw error;
      return data as ScrapedPage | null;
    },
    staleTime: STALE_TIME,
  });
}

/** Query options för alla publicerade skrapade sidor av en typ (listsidor). */
export function scrapedListQueryOptions(
  type: string,
  orderBy: "title" | "published_at" = "title",
) {
  return queryOptions({
    queryKey: ["scraped-list", type, orderBy],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scraped_pages")
        .select("id, type, slug, path, title, meta_description, hero_image, published_at")
        .eq("type", type)
        .eq("is_published", true)
        .order(orderBy === "published_at" ? "published_at" : "title", {
          ascending: orderBy !== "published_at",
          nullsFirst: false,
        });
      if (error) throw error;
      return (data ?? []) as Partial<ScrapedPage>[];
    },
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch a single published scraped page by its path.
 * `initialData` kan matas från en route loader (SSR) så att sidan renderas
 * direkt utan att layouten hoppar till efter hydrering.
 */
export function useScrapedPage(path: string, initialData?: ScrapedPage | null) {
  return useQuery({
    ...scrapedPageQueryOptions(path),
    // Även null (saknad sida) är ett giltigt SSR-värde – annars renderar
    // klienten en laddningsindikator som inte matchar serverns HTML.
    ...(initialData !== undefined ? { initialData } : {}),
  });
}

/** Fetch all published scraped pages of a given type (for list pages). */
export function useScrapedList(
  type: string,
  orderBy: "title" | "published_at" = "title",
) {
  return useQuery(scrapedListQueryOptions(type, orderBy));
}

/** Fetch published scraped pages whose path starts with a given prefix. */
export function useScrapedPathList(prefix: string, limit?: number) {
  return useQuery({
    queryKey: ["scraped-path-list", prefix, limit ?? null],
    queryFn: async () => {
      let query = supabase
        .from("scraped_pages")
        .select("id, type, slug, path, title, meta_description, hero_image, published_at")
        .like("path", `${prefix}/%`)
        .eq("is_published", true)
        .order("published_at", { ascending: false, nullsFirst: false });
      if (limit) query = query.limit(limit);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Partial<ScrapedPage>[];
    },
    staleTime: STALE_TIME,
  });
}
